<script setup lang="ts">
import Column from 'primevue/column'
import {
  getDownloadCompanyImageUrl,
  useListAdminMatchingJobCategories,
  useListAdminMatchingJobFunctions,
  useListCompanyMedia,
  useLookupAdminCompanies,
} from '@swipe4work/api-orval'
import {
  AdminPermissionResource,
  type ListCompanyMediaCompanyMediaDto,
  ListCompanyMediaSortField,
  PermissionLevel,
} from '@swipe4work/models-orval'
import CompanyMediaFilters from '@/features/companies/components/CompanyMediaFilters.vue'
import CompanyMediaPreviewDialog from '@/features/companies/dialogs/CompanyMediaPreviewDialog.vue'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useCompanyActions } from '@/features/companies/composables/useCompanyActions'
import { usePermissions } from '@/shared/composables/UsePermissions'

definePage({
  path: '/companies/media',
  name: 'companies.media',
  meta: {
    title: 'Bedrijfsmedia',
    breadcrumbParent: 'companies.overview',
    permission: {
      resource: 'CompanyMedia',
      level: 'Read',
    },
  },
})

const PAGE_SIZE = 10
const MAX_INLINE_IMAGES = 4

const router = useRouter()
const dialog = useDynamicDialog()
const companyActions = useCompanyActions()

const { can } = usePermissions()
const canEditVideo = computed(() => can(AdminPermissionResource.CompanyMedia, PermissionLevel.Write))

type FilterState = {
  companyId: number | null
  jobCategoryIds: number[]
  jobFunctionIds: number[]
  hasVideo: string | null
  hasImages: string | null
  sort: ListCompanyMediaSortField
  sortDescending: boolean
}

function emptyFilters(): FilterState {
  return {
    companyId: null,
    jobCategoryIds: [],
    jobFunctionIds: [],
    hasVideo: null,
    hasImages: null,
    sort: ListCompanyMediaSortField.CompanyName,
    sortDescending: false,
  }
}

const {
  filters,
  draft,
  searchInput,
  search,
  offset,
  activeFilterCount,
  onFilterPanelShow,
  onFilterApply,
  onFilterClear,
  onPage,
} = useDataTableFilters<FilterState>({
  schema: {
    companyId: 'id',
    jobCategoryIds: 'ids',
    jobFunctionIds: 'ids',
    hasVideo: 'enum',
    hasImages: 'enum',
    sort: 'enum',
    sortDescending: 'boolean',
  },
  defaultFilters: emptyFilters,
  defaultPageSize: PAGE_SIZE,
})

function toTriState(value: string | null): boolean | undefined {
  if (value === 'yes') return true
  if (value === 'no') return false

  return undefined
}

const { data, isPending, refetch } = useListCompanyMedia(
  computed(() => ({
    Search: search.value || undefined,
    CompanyId: filters.companyId ?? undefined,
    JobCategoryIds: filters.jobCategoryIds.length > 0 ? filters.jobCategoryIds : undefined,
    JobFunctionIds: filters.jobFunctionIds.length > 0 ? filters.jobFunctionIds : undefined,
    HasVideo: toTriState(filters.hasVideo),
    HasImages: toTriState(filters.hasImages),
    Sort: filters.sort,
    SortDescending: filters.sortDescending,
    Limit: PAGE_SIZE,
    Offset: offset.value,
  })),
  { query: { staleTime: 30 * 1000 } },
)

/*
 * A thumbnail filename that no longer exists on BunnyCDN 404s; tracking the failures turns the silently
 * broken image into something a marketeer can spot and fix.
 */
const brokenThumbnails = ref(new Set<number>())

watch(data, () => {
  brokenThumbnails.value = new Set()
})

const companies = computed(() => data.value?.companies ?? [])
const totalCount = computed(() => data.value?.totalCount ?? 0)
const withVideoCount = computed(() => data.value?.withVideoCount ?? 0)

const { data: companiesLookupData } = useLookupAdminCompanies({ query: { staleTime: 5 * 60 * 1000 } })
const { data: jobCategoriesData } = useListAdminMatchingJobCategories({ query: { staleTime: Infinity } })
const { data: jobFunctionsData } = useListAdminMatchingJobFunctions({ query: { staleTime: Infinity } })

const companyOptions = computed(() => companiesLookupData.value?.companies ?? [])
const jobCategoryOptions = computed(() => jobCategoriesData.value?.jobCategories ?? [])
const allJobFunctions = computed(() => jobFunctionsData.value?.jobFunctions ?? [])

function openPreview(media: ListCompanyMediaCompanyMediaDto) {
  dialog.open(CompanyMediaPreviewDialog, { data: { media } })
}

function openCompany(companyId: number) {
  router.push({
    name: 'companies.[id]',
    params: { id: companyId },
  })
}

function editVideo(companyId: number) {
  companyActions.openUpdateVideoDialog(companyId, {
    onUpdate: async () => {
      await refetch()
    },
  })
}
</script>

<template>
  <Card>
    <template #content>
      <section class="flex flex-wrap items-center gap-3 pb-4">
        <DataTableFilterPanel
          :active-filter-count="activeFilterCount"
          @show="onFilterPanelShow"
          @apply="onFilterApply"
          @clear="onFilterClear"
        >
          <CompanyMediaFilters
            :draft="draft"
            :company-options="companyOptions"
            :job-category-options="jobCategoryOptions"
            :all-job-functions="allJobFunctions"
          />
        </DataTableFilterPanel>

        <IconField class="max-w-xs flex-1">
          <InputIcon class="fa-regular fa-search" />
          <InputText
            v-model="searchInput"
            placeholder="Zoek bedrijf..."
            class="w-full"
          />
        </IconField>

        <span class="text-sm text-surface-500">
          {{ totalCount }} bedrijven · {{ withVideoCount }} met video
        </span>
      </section>

      <DataTable
        :value="companies"
        :loading="isPending"
        :lazy="true"
        :first="offset"
        :rows="PAGE_SIZE"
        :total-records="totalCount"
        paginator
        size="small"
        row-hover
        data-key="companyId"
        class="w-full"
        @page="onPage"
      >
        <template #empty>
          <div class="py-12 text-center text-surface-500">
            Geen bedrijven gevonden
          </div>
        </template>

        <Column field="companyName" header="Bedrijf" style="width: 16rem">
          <template #body="{ data: media }">
            <button
              class="flex items-center gap-2 text-left"
              @click="openCompany(media.companyId)"
            >
              <Avatar
                v-if="media.logoUrl"
                :image="media.logoUrl"
                shape="circle"
                :pt="{
                  root: { class: '!size-8 !bg-white !border !border-surface-200' },
                  image: { class: 'object-contain' },
                }"
              />
              <Avatar
                v-else
                :label="media.companyName.charAt(0).toUpperCase()"
                shape="circle"
                :pt="{ root: { class: '!size-8 !bg-primary-100 !text-primary-700' } }"
              />
              <span class="text-sm font-medium">{{ media.companyName }}</span>
            </button>
          </template>
        </Column>

        <Column header="Video" style="width: 8rem">
          <template #body="{ data: media }">
            <Tag
              :value="media.hasVideo ? 'Video' : 'Geen video'"
              :severity="media.hasVideo ? 'success' : 'secondary'"
            />
          </template>
        </Column>

        <Column header="BunnyCDN ID" style="width: 12rem">
          <template #body="{ data: media }">
            <span v-if="media.hasVideo" class="truncate text-xs font-mono">{{ media.bunnyCdnFileId }}</span>
            <span v-else class="text-xs text-surface-400">—</span>
          </template>
        </Column>

        <Column header="Thumbnail" style="width: 9rem">
          <template #body="{ data: media }">
            <PreviewImage
              v-if="media.hasVideo && media.videoThumbnailUrl && !brokenThumbnails.has(media.companyId)"
              :src="media.videoThumbnailUrl"
              :alt="`Thumbnail van de bedrijfsvideo van ${media.companyName}`"
              image-class="h-12 w-20 rounded-md object-cover"
              @error="brokenThumbnails.add(media.companyId)"
            />
            <Tag
              v-else-if="media.hasVideo"
              value="Niet gevonden"
              severity="warn"
              v-tooltip.top="'De ingestelde thumbnail bestaat niet op BunnyCDN'"
            />
            <span v-else class="text-xs text-surface-400">—</span>
          </template>
        </Column>

        <Column header="Afbeeldingen">
          <template #body="{ data: media }">
            <div v-if="media.images.length" class="flex items-center gap-2">
              <PreviewImage
                v-for="image in media.images.slice(0, MAX_INLINE_IMAGES)"
                :key="image.image.id"
                :src="image.image.url"
                :alt="image.image.fileName"
                :file-name="image.image.fileName"
                :download-path="getDownloadCompanyImageUrl(image.image.id)"
                image-class="h-12 w-16 rounded-md object-cover"
              />
              <Button
                v-if="media.images.length > MAX_INLINE_IMAGES"
                :label="`+${media.images.length - MAX_INLINE_IMAGES}`"
                text
                size="small"
                @click="openPreview(media)"
              />
            </div>
            <span v-else class="text-xs text-surface-400">Geen afbeeldingen</span>
          </template>
        </Column>

        <Column header="" style="width: 11rem">
          <template #body="{ data: media }">
            <div class="flex justify-end gap-1">
              <Button
                icon="fa-regular fa-play"
                text
                severity="secondary"
                v-tooltip.top="media.hasVideo ? 'Bekijk media' : 'Bekijk afbeeldingen'"
                @click="openPreview(media)"
              />
              <Button
                v-if="canEditVideo"
                icon="fa-regular fa-film"
                text
                severity="secondary"
                v-tooltip.top="media.hasVideo ? 'Video wijzigen' : 'Video toevoegen'"
                @click="editVideo(media.companyId)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>
